import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Tooltip as ReactToolTip } from "react-tooltip";
import { TiPin } from "react-icons/ti";
import { RiUnpinFill } from "react-icons/ri";
import { LuLogOut } from "react-icons/lu";

import { links } from "../../constants/data";
import { useTranslation } from "react-i18next";

const Sidebar = ({ isExpanded, setIsExpanded, signOut }) => {
  const { t } = useTranslation();
  const isPinnedFromLocalStorage = localStorage.getItem("isPinned") === "true";
  const [isPinned, setIsPinned] = useState(isPinnedFromLocalStorage);

  const activeLink =
    "flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold bg-indigo-600 text-white shadow-xs shadow-indigo-600/20 transition-all duration-150 my-1";
  const activeLinkSmall =
    "flex items-center justify-center w-11 h-11 mx-auto my-1.5 rounded-xl text-lg bg-indigo-600 text-white shadow-xs shadow-indigo-600/20 transition-all duration-150";

  const normalLink =
    "flex items-center gap-3.5 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800/70 transition-all duration-150 my-1 group";
  const normalLinkSmall =
    "flex items-center justify-center w-11 h-11 mx-auto my-1.5 rounded-xl text-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800/70 transition-all duration-150 group";

  const togglePin = () => {
    const nextVal = !isPinned;
    setIsPinned(nextVal);
    localStorage.setItem("isPinned", nextVal ? "true" : "false");
  };

  return (
    <aside
      onMouseEnter={() => {
        if (!isPinned) setIsExpanded(true);
      }}
      onMouseLeave={() => {
        if (!isPinned) setIsExpanded(false);
      }}
      className={`hidden md:flex flex-col h-screen overflow-y-auto overflow-x-hidden bg-[#0f172a] border-r border-slate-800/90 text-slate-300 transition-all duration-300 select-none ${
        isExpanded ? "w-72 p-4" : "w-24 p-3"
      }`}
    >
      {/* 1. Header / Brand & Pin Toggle */}
      <div
        className={`flex items-center pb-3.5 mb-2 border-b border-slate-800/80 ${
          isExpanded ? "justify-between" : "justify-center"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-indigo-500 flex items-center justify-center text-white font-bold text-sm shadow-xs shadow-indigo-500/20 shrink-0">
            P
          </div>
          {isExpanded && (
            <div className="flex flex-col overflow-hidden">
              <span className="font-bold text-sm text-white tracking-tight leading-tight">
                PROGNOS
              </span>
              <span className="text-[10px] text-slate-400 font-medium">
                Admin Panel
              </span>
            </div>
          )}
        </div>

        {isExpanded && (
          <div>
            <button
              id="sidebar-pin-btn"
              type="button"
              aria-label="Pin sidebar"
              onClick={togglePin}
              className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                isPinned
                  ? "bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              {!isPinned ? (
                <TiPin size={18} />
              ) : (
                <RiUnpinFill size={18} />
              )}
            </button>
            <ReactToolTip
              anchorSelect="#sidebar-pin-btn"
              content={isPinned ? t("sidebar.shrink") || "Ապաամրացնել" : t("sidebar.expand") || "Ամրացնել"}
              variant="dark"
              place="bottom"
              style={{ zIndex: 999, fontSize: "11px", padding: "4px 8px" }}
            />
          </div>
        )}
      </div>

      {/* 2. Navigation Items */}
      <div className="flex-1 flex flex-col justify-between overflow-y-auto overflow-x-hidden pt-2">
        <nav className="space-y-4">
          {links.map((section, idx) => (
            <div key={section.title || idx}>
              {isExpanded ? (
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400/70 px-3 mb-1.5">
                  {t(section.title)}
                </h4>
              ) : (
                idx > 0 && <div className="my-2.5 mx-2 border-t border-slate-800/80" />
              )}

              <div className="space-y-0.5">
                {section.links.map((link) => {
                  const label = t(link.name);
                  return link.type === "route" ? (
                    <NavLink
                      to={link.path}
                      key={link.name}
                      title={!isExpanded ? label : undefined}
                      className={({ isActive }) =>
                        isActive
                          ? isExpanded
                            ? activeLink
                            : activeLinkSmall
                          : isExpanded
                          ? normalLink
                          : normalLinkSmall
                      }
                    >
                      <span className="text-lg shrink-0 flex items-center justify-center">
                        {link.icon}
                      </span>
                      {isExpanded && (
                        <span className="capitalize text-sm truncate font-medium">
                          {label}
                        </span>
                      )}
                    </NavLink>
                  ) : (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={link.name}
                      title={!isExpanded ? label : undefined}
                      className={isExpanded ? normalLink : normalLinkSmall}
                    >
                      <span className="text-lg shrink-0 flex items-center justify-center">
                        {link.icon}
                      </span>
                      {isExpanded && (
                        <span className="capitalize text-sm truncate font-medium">
                          {label}
                        </span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* 3. Bottom Sign Out Action */}
        <div className="pt-4 pb-2 border-t border-slate-800/80 mt-6">
          <button
            onClick={signOut}
            type="button"
            title={!isExpanded ? t("sidebar.signOut") || "Ելք" : undefined}
            className={`cursor-pointer transition-all duration-150 font-medium ${
              isExpanded
                ? "w-full flex items-center justify-center gap-2.5 px-3 py-2.5 rounded-xl text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 border border-rose-500/20 shadow-xs"
                : "w-11 h-11 mx-auto flex items-center justify-center rounded-xl text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 border border-rose-500/20"
            }`}
          >
            <LuLogOut size={16} />
            {isExpanded && (
              <span className="text-xs font-semibold">
                {t("sidebar.signOut")}
              </span>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
